import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Box, CardMedia } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoResults from '../../components/reusableComponents/NoResults';
import { PulsateCardSkeleton } from '../../components/reusableComponents/SkeletonCard';
import ShareMenu from '../dashboardDemo/ShareMenu';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PlaylistPage({ playlistItems, skeleton }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlaylistUrl, setSelectedPlaylistUrl] = useState('https://gntunes.com/playlist');
  const navigate = useNavigate();

  // ref for the scroll container
  const scrollRef = useRef(null);

  const handleShareClick = (event, slug) => {
    setAnchorEl(event.currentTarget);
    setSelectedPlaylistUrl(`https://gntunes.com/playlist/${slug}`);
  };

  // Scroll left by one card width (170px)
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -170, behavior: 'smooth' });
    }
  };

  // Scroll right by one card width (170px)
  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 170, behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        height: { xs: '100%', md: '485px' },
        backgroundColor: '#FFFFFF',
        padding: '19px 18px',
        gap: '31px',
        borderRadius: '10px',
      }}
    >
      {/* Header with title and See all button */}
      <Box justifyContent={'space-between'} display={'flex'}>
        <Typography variant="h6" sx={{ color: '#1B2559', fontSize: '20px', fontWeight: 500 }}>
          Official Playlist
        </Typography>
        <a href="https://gntunes.com/playlist" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
          <Button
            size="small"
            sx={{
              width: '78px',
              height: '32px',
              color: '#4318FF',
              backgroundColor: '#F4F7FE',
              textTransform: 'capitalize',
              borderRadius: '35px',
              fontSize: '14px',
            }}
          >
            See all
          </Button>
        </a>
      </Box>

      {/* Slider container with arrows */}
      <Box position="relative" sx={{ paddingTop: '25px' }}>
        {/* Left scroll arrow */}
        <Box
          onClick={scrollLeft}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.7)',
            borderRadius: '50%',
            cursor: 'pointer',
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            boxShadow: 1,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
          }}
        >
          <ChevronLeftIcon />
        </Box>

        {/* Cards scroll container */}
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: 2,
            pb: 1,
            maxWidth: '700px',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            // '-ms-overflow-style': 'none',
            // 'scrollbar-width': 'none',
          }}
        >
          {skeleton ? (
            <PulsateCardSkeleton />
          ) : playlistItems ? (
            playlistItems.map((item, i) => (
             item?.privacy_status && <Card
                key={i}
                onClick={() => window.open(`https://gntunes.com/playlist/${item.slug}`, '_blank')}
                sx={{
                  width: '170px',
                  flexShrink: 0,
                  height: '262px',
                  border: '1px solid #D9D9D9',
                  borderRadius: '10px',
                  cursor:'pointer'
                }}
              >
                {/* Playlist card image with share icon */}
                <Box sx={{ height: '170px', width: '170px', position: 'relative' }}>
                  <CardMedia
                    component="img"
                    image={item.image}
                    alt={item.title || 'Playlist Image'}
                    height="170"
                    sx={{
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background:
                        'linear-gradient(98.85deg, #FFFFFF 3.36%, rgba(255, 255, 255, 0) 238.16%)',
                      borderRadius: '6.17px',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backdropFilter: 'blur(8.8px)',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShareClick(e, item.slug);
                    }}
                  >
                    <ShareIcon sx={{ fontSize: 16, color: '#ED3237' }} />
                  </Box>
                </Box>

                {/* Playlist card content */}
                <CardContent>
                  <Typography fontWeight={500} fontSize={14} sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', }}>
                    {item.title}
                  </Typography>
                  <Box display="flex" gap={0.5}>
                    {item.platforms?.map(({ icon, link }, index) =>
                      icon && link ? (
                        <a
                          key={index}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none' }}
                        >
                          <Box sx={{
                            '&:hover': {
                              borderRadius: '50%',
                              height: '28px',
                              width: '28px',
                              border: '1px solid  #F02727'
                            },
                          }}>
                            <img
                              src={icon}
                              alt="platform-icon"
                              style={{ height: '28px', width: '28px' }}
                            />
                          </Box>

                        </a>
                      ) : null
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <NoResults />
          )}
        </Box>

        {/* Right scroll arrow */}
        <Box
          onClick={scrollRight}
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.7)',
            borderRadius: '50%',
            cursor: 'pointer',
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            boxShadow: 1,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
          }}
        >
          <ChevronRightIcon />
        </Box>
      </Box>

      {/* Submit song card */}
      <Card
        sx={{
          background: 'linear-gradient(to right, #111827, #1e3a8a)',
          color: 'white',
          p: 2,
          mt: 5,
          display: 'flex',
          alignItems: 'center',
          textAlign: 'center',
          justifyContent: { xs: 'none', md: 'space-between' },
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
          textAlign={{ xs: 'left', md: 'center' }}
          gap={{ xs: 1, md: 3 }}
        >
          <Typography
            variant="body1"
            sx={{ fontSize: { xs: '14px', md: '14px' }, fontWeight: 400, paddingTop: '5px' }}
          >
            Submit your song — Get featured on our Playlist, seen by artists, fans, and on Spotify.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/releasealbum')}
            sx={{
              backgroundColor: '#ED3237',
              width: '116px',
              borderRadius: '8px',
              fontSize: '13px',
              '&:hover': {
                backgroundColor: '#ED3237',
              },
            }}
          >
            SUBMIT NOW
          </Button>
        </Box>
      </Card>

      {/* Share menu */}
      <ShareMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} playlistUrl={selectedPlaylistUrl} />
    </Box>
  );
}
